import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, MouseEvent } from 'react'

import './TagsInput.css'

interface TagsInputProps {
  id: string
  values: string[]
  onChange: (values: string[]) => void
  suggestions?: string[]
  placeholder?: string
  disabled?: boolean
}

const MAX_SUGGESTIONS = 8

export default function TagsInput({
  id,
  values,
  onChange,
  suggestions = [],
  placeholder,
  disabled = false
}: TagsInputProps) {
  const [text, setText] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const addValue = (raw: string) => {
    const value = raw.trim()
    setText('')
    setIsOpen(false)
    if (!value || values.includes(value)) return
    onChange([...values, value])
  }

  const removeValue = (value: string) => {
    onChange(values.filter((v) => v !== value))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addValue(text)
    } else if (e.key === 'Backspace' && text === '' && values.length > 0) {
      removeValue(values[values.length - 1])
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  // Keep the dropdown open through the click by not letting the input blur first.
  const handleSuggestionMouseDown = (e: MouseEvent) => {
    e.preventDefault()
  }

  const excludeSet = new Set(values)
  const needle = text.trim().toLowerCase()
  const options = suggestions
    .filter((s) => !excludeSet.has(s) && s.toLowerCase().includes(needle))
    .slice(0, MAX_SUGGESTIONS)

  return (
    <div
      className={`tags-input${disabled ? ' disabled' : ''}`}
      ref={containerRef}
    >
      <div className="tags-input-box">
        {values.map((value) => (
          <span key={value} className="chip">
            <span>{value}</span>
            <button
              type="button"
              className="remove-chip"
              disabled={disabled}
              onClick={() => removeValue(value)}
            >
              <span className="icon icon-cross" />
            </button>
          </span>
        ))}
        <input
          id={id}
          type="text"
          value={text}
          disabled={disabled}
          placeholder={values.length === 0 ? placeholder : ''}
          onChange={(e) => {
            setText(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
      </div>
      {isOpen && options.length > 0 && (
        <ul className="tags-suggestions">
          {options.map((option) => (
            <li key={option}>
              <button
                type="button"
                onMouseDown={handleSuggestionMouseDown}
                onClick={() => addValue(option)}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
